import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/cn'
import { MovingBorder } from '@/components/aceternity/moving-border'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium font-sans transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-salmon focus-visible:ring-offset-2 focus-visible:ring-offset-paper aria-invalid:ring-burn/20 aria-invalid:border-burn",
  {
    variants: {
      variant: {
        default:
          'bg-salmon text-white hover:bg-salmon/90',
        destructive:
          'bg-burn text-white hover:bg-burn/90',
        outline:
          'border border-ink bg-transparent text-ink hover:bg-ink hover:text-paper',
        secondary:
          'bg-vellum text-ink border border-rule hover:bg-rule/50',
        ghost:
          'text-ink hover:bg-vellum hover:text-ink',
        link:
          'text-salmon-text underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-sm gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-sm px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export function MovingBorderButton({
  children,
  className,
  borderRadius,
  duration,
  ...props
}: React.ComponentProps<'button'> & { borderRadius?: string; duration?: number }): React.ReactElement {
  return (
    <MovingBorder
      borderRadius={borderRadius ?? '0.25rem'}
      duration={duration ?? 3000}
      className={cn(
        'bg-paper text-ink font-sans font-semibold px-6 py-3',
        className
      )}
      {...props}
    >
      {children}
    </MovingBorder>
  );
}

export { Button, buttonVariants }
